import React, { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useDebounce } from '../../utils/debounce';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Chip,
    Paper,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Alert,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from '@mui/material';
import { Search, PictureAsPdf, Fingerprint } from '@mui/icons-material';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';
import api from '../../api/axios';

interface AuditLog {
    id: number;
    uuid: string;
    pdf_type: string;
    record_id: number;
    record_name: string | null;
    user_id: number;
    user_name: string;
    tenant_id: number | null;
    tenant_name: string | null;
    ip_address: string | null;
    user_agent: string | null;
    generated_at: string;
}

const pdfTypeLabels: Record<string, string> = {
    citizen: 'Citizen',
    complaint: 'Complaint',
    development_activity: 'Development Activity',
    employment_activity: 'Employment Activity',
};

export default function PdfAuditLogs() {
    const [search, setSearch] = useState('');
    const [pdfTypeFilter, setPdfTypeFilter] = useState('');
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
    const [lookupUuid, setLookupUuid] = useState('');
    const [lookupResult, setLookupResult] = useState<AuditLog | null>(null);
    const [lookupError, setLookupError] = useState('');
    const [lookupOpen, setLookupOpen] = useState(false);
    const debouncedSearch = useDebounce(search, 400);

    const { data, isLoading } = useQuery({
        queryKey: ['pdf-audit-logs', debouncedSearch, pdfTypeFilter, paginationModel.page, paginationModel.pageSize],
        queryFn: async () => {
            const params: Record<string, string | number> = {
                page: paginationModel.page + 1,
                per_page: paginationModel.pageSize,
            };
            if (debouncedSearch) params.search = debouncedSearch;
            if (pdfTypeFilter) params.pdf_type = pdfTypeFilter;
            const res = await api.get('/superadmin/pdf-audit-logs', { params });
            return res.data.data;
        },
        placeholderData: keepPreviousData,
    });

    const handleLookup = async () => {
        const trimmed = lookupUuid.trim();
        if (!trimmed) return;
        setLookupError('');
        setLookupResult(null);
        try {
            const res = await api.get('/superadmin/pdf-audit-logs/lookup', { params: { uuid: trimmed } });
            setLookupResult(res.data.data);
        } catch {
            setLookupError('No PDF found with this Document ID');
        }
    };

    const columns: GridColDef[] = [
        {
            field: 'uuid',
            headerName: 'Document ID',
            flex: 1.3,
            minWidth: 200,
            renderCell: (params: GridRenderCellParams) => (
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#475569' }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'pdf_type',
            headerName: 'Type',
            width: 160,
            renderCell: (params: GridRenderCellParams) => (
                <Chip label={pdfTypeLabels[params.value] || params.value} size="small" variant="outlined" />
            ),
        },
        { field: 'record_name', headerName: 'Record', flex: 0.8, minWidth: 120 },
        { field: 'user_name', headerName: 'Generated By', flex: 0.7, minWidth: 120 },
        { field: 'tenant_name', headerName: 'Tenant', flex: 0.7, minWidth: 120, renderCell: (p: GridRenderCellParams) => p.value || '—' },
        { field: 'ip_address', headerName: 'IP Address', width: 130 },
        {
            field: 'generated_at',
            headerName: 'Date & Time',
            width: 170,
            renderCell: (params: GridRenderCellParams) => {
                if (!params.value) return '—';
                return new Date(params.value).toLocaleString('en-IN', {
                    day: '2-digit', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit', hour12: true,
                });
            },
        },
    ];

    return (
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 3, mb: 3, borderRadius: 2,
                    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                    color: 'white',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.15)', width: 48, height: 48 }}>
                        <Fingerprint />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h4" fontWeight={700}>PDF Audit Logs</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                            Track and verify every PDF generated across all tenants
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Lookup Section */}
            <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#475569' }}>
                    Verify a Document ID
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <TextField
                        size="small"
                        placeholder="Paste Document ID (UUID) from PDF footer"
                        value={lookupUuid}
                        onChange={(e) => setLookupUuid(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                        sx={{ flex: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                            ),
                        }}
                    />
                    <Button variant="contained" onClick={handleLookup} disabled={!lookupUuid.trim()} sx={{ textTransform: 'none' }}>
                        Lookup
                    </Button>
                </Box>
                {lookupError && <Alert severity="error" sx={{ mt: 1.5 }}>{lookupError}</Alert>}
                {lookupResult && (
                    <Paper variant="outlined" sx={{ mt: 2, p: 0, overflow: 'hidden' }}>
                        <Box sx={{ bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', px: 2, py: 1.5 }}>
                            <Typography variant="subtitle2" color="success.main">Match Found</Typography>
                        </Box>
                        <Table size="small">
                            <TableBody>
                                <TableRow><TableCell sx={{ color: '#64748b', width: 150 }}>Document ID</TableCell><TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{lookupResult.uuid}</TableCell></TableRow>
                                <TableRow><TableCell sx={{ color: '#64748b' }}>PDF Type</TableCell><TableCell>{pdfTypeLabels[lookupResult.pdf_type] || lookupResult.pdf_type}</TableCell></TableRow>
                                <TableRow><TableCell sx={{ color: '#64748b' }}>Record</TableCell><TableCell>{lookupResult.record_name || '—'} (ID: {lookupResult.record_id})</TableCell></TableRow>
                                <TableRow><TableCell sx={{ color: '#64748b' }}>Generated By</TableCell><TableCell>{lookupResult.user_name} (User ID: {lookupResult.user_id})</TableCell></TableRow>
                                <TableRow><TableCell sx={{ color: '#64748b' }}>Tenant</TableCell><TableCell>{lookupResult.tenant_name || '—'}{lookupResult.tenant_id ? ` (ID: ${lookupResult.tenant_id})` : ''}</TableCell></TableRow>
                                <TableRow><TableCell sx={{ color: '#64748b' }}>IP Address</TableCell><TableCell>{lookupResult.ip_address || '—'}</TableCell></TableRow>
                                <TableRow><TableCell sx={{ color: '#64748b' }}>User Agent</TableCell><TableCell sx={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{lookupResult.user_agent || '—'}</TableCell></TableRow>
                                <TableRow><TableCell sx={{ color: '#64748b' }}>Generated At</TableCell><TableCell>{new Date(lookupResult.generated_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}</TableCell></TableRow>
                            </TableBody>
                        </Table>
                    </Paper>
                )}
            </Paper>

            {/* Filters Row */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                    size="small"
                    placeholder="Search by UUID, user, tenant, IP..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ flex: 1, maxWidth: 400 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                        ),
                    }}
                />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>PDF Type</InputLabel>
                    <Select
                        value={pdfTypeFilter}
                        label="PDF Type"
                        onChange={(e) => setPdfTypeFilter(e.target.value)}
                    >
                        <MenuItem value="">All Types</MenuItem>
                        <MenuItem value="citizen">Citizen</MenuItem>
                        <MenuItem value="complaint">Complaint</MenuItem>
                        <MenuItem value="development_activity">Development Activity</MenuItem>
                        <MenuItem value="employment_activity">Employment Activity</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Data Grid */}
            <DataGrid
                rows={data?.data || []}
                columns={columns}
                rowCount={data?.total || 0}
                loading={isLoading}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[25, 50, 100]}
                disableRowSelectionOnClick
                autoHeight
                sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 2,
                    '& .MuiDataGrid-columnHeaders': { bgcolor: '#f8fafc' },
                }}
            />
        </Box>
    );
}
